import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

interface UseAuthOptions {
  /** If true, redirects to /auth when not authenticated */
  requireAuth?: boolean;
  /** Path to redirect to when not authenticated (default: /auth) */
  redirectTo?: string;
}

interface UseAuthReturn {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

/**
 * Hook to manage authentication state.
 * Uses onAuthStateChange as the single source of truth to avoid race conditions.
 */
export function useAuth(options: UseAuthOptions = {}): UseAuthReturn {
  const { requireAuth = false, redirectTo = "/auth" } = options;
  const navigate = useNavigate();
  
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener - this fires immediately with current session
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        setIsLoading(false);

        // Handle sign out - redirect to home
        if (event === "SIGNED_OUT" && requireAuth) {
          navigate("/", { replace: true });
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate, requireAuth]);

  // Handle redirect for unauthenticated users (after loading completes)
  useEffect(() => {
    if (!isLoading && requireAuth && !session) {
      navigate(redirectTo, { replace: true });
    }
  }, [isLoading, requireAuth, session, navigate, redirectTo]);

  return {
    user,
    session,
    isLoading,
    isAuthenticated: !!session,
  };
}
